import snakeCase from "lodash/snakeCase";
import {
  LeaderboardUser,
  TRANSACTION_TYPES_BY_INDEX,
  TRANSACTION_TYPES_EXTENDED,
} from "../types";
import { desoNanosToUSD, formatUSD } from "./currency";
import { DeSoNetwork, publicKeyToBase58Check } from "deso-protocol";
import { DateRange } from "react-day-picker";
import dayjs from "dayjs";
import { CoreAccountFieldsFragment } from "../graphql/codegen/graphql";
import { uniqBy } from "lodash";

export const shortenLongWord = (
  key?: string | null,
  endFirstPartAfter = 4,
  startSecondPartAfter = 4,
  separator = "...",
) => {
  if (!key || key.length <= endFirstPartAfter + startSecondPartAfter) {
    return key || "";
  }

  return [
    key.slice(0, endFirstPartAfter),
    separator,
    key.slice(-startSecondPartAfter),
  ].join("");
};

export function bytesToBase64(bytes: Uint8Array): string {
  const binString = Array.from(bytes, (x) => String.fromCodePoint(x)).join("");
  return btoa(binString);
}

export function range(num: number) {
  return Array.from(Array(num).keys());
}

export function bytesToHex(byteArray: Uint8Array) {
  return Array.from(byteArray, function (byte) {
    return ("0" + (byte & 0xff).toString(16)).slice(-2);
  }).join("");
}

export function abbreviateNumber(value: number): string {
  return Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatTxnType(txnType?: number | null) {
  return txnType !== null && txnType !== undefined
    ? snakeCase(
        TRANSACTION_TYPES_BY_INDEX[txnType].replace("TxnType", ""),
      ).toUpperCase()
    : "Unknown";
}

export function formatTxnFees(feeNanos: number, exchangeRate: number) {
  const feeUsdNanos = desoNanosToUSD(feeNanos, exchangeRate);
  const feeUsdFormatted = formatUSD(feeUsdNanos, true, 7);

  return feeUsdFormatted;
}

export function getTransactionInfo(index: number) {
  const typeByIndex = TRANSACTION_TYPES_BY_INDEX[index];
  return TRANSACTION_TYPES_EXTENDED.find((e) => e.type === typeByIndex);
}

export const publicKeyByteArrayToBase58Check = (byteArray: Array<number>) => {
  return publicKeyToBase58Check(Uint8Array.from(byteArray));
};

export const formatTxnUsername = (key: string) => {
  if (!key) {
    return "";
  }
  const encoded = Uint8Array.from(atob(key), (c) => c.charCodeAt(0));
  return publicKeyToBase58Check(encoded, {
    network: DeSoNetwork.mainnet,
  });
};

export const isDesoPublicKey = (value: string) => {
  // User public key: Starts with 'BC', length 55, base58 characters
  return /^BC[1-9A-HJ-NP-Za-km-z]{53}$/.test(value);
};

export const isBlockHash = (value: string) => {
  // Block hash: Hex string of length 64
  return /^[0-9A-Fa-f]{64}$/.test(value);
};

export const copyTextToClipboard = async (text: string) => {
  if ("clipboard" in navigator) {
    return await navigator.clipboard.writeText(text);
  } else {
    return document.execCommand("copy", true, text);
  }
};

export const formatDisplayName = (
  username?: string | null,
  publicKey?: string | null,
  addAt = false,
  shorten = true,
) => {
  let result = "";

  if (username) {
    result = `${addAt ? "@" : ""}${username}`;
  } else if (publicKey) {
    result = shorten ? shortenLongWord(publicKey) : publicKey;
  }

  return result;
};

export const parseLeaderboardUser = <T>(
  rawValue: T,
  publicKey?: string | null,
  username?: string | null,
  value?: string | null,
): LeaderboardUser<T> => {
  return {
    publicKey: publicKey || "",
    username: username || "",
    value: value || "",
    rawValue,
  };
};

export const formatDateFiltersPayload = (date: DateRange) => {
  return {
    greaterThanOrEqualTo: dayjs(date.from).startOf("day").toISOString(),
    lessThan: dayjs(date.to).endOf("day").toISOString(),
  };
};

export const concatUserResponses = (
  exactMatch?: CoreAccountFieldsFragment | null,
  usersList?: Array<CoreAccountFieldsFragment | null>,
) => {
  const nonEmptyUsers = (usersList || []).filter((e) => e !== null);

  return uniqBy(
    [exactMatch, ...nonEmptyUsers].filter((e) => !!e && e.publicKey),
    "publicKey",
  ) as Array<CoreAccountFieldsFragment>;
};
